import React, { useState, useEffect } from 'react';

const ApplicationStatusChecker = ({ onNavigate }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [application, setApplication] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleCheckStatus = async () => {
    if (!applicationNumber || applicationNumber.length < 10) {
      setError('Please enter a valid application number');
      return;
    }

    setLoading(true);
    setError('');
    setApplication(null);

    try {
      const response = await fetch(`${API_BASE_URL}/registration/status/${applicationNumber}`);
      const data = await response.json();

      if (response.ok && data.success) {
        setApplication(data.application);
      } else {
        setError(data.message || 'Application not found');
      }
    } catch (err) {
      console.error('Error checking status:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#f59e0b';
      case 'under-review':
        return '#3b82f6';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'under-review':
        return '🔍';
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      default:
        return '📄';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: isMobile ? '20px' : '40px 20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    },
    header: {
      maxWidth: '800px',
      margin: '0 auto 30px auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    backButton: {
      background: 'white',
      border: '2px solid #e5e7eb',
      color: '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '10px 20px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '10px',
      transition: 'all 0.3s ease'
    },
    mainCard: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: isMobile ? '24px' : '40px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
    },
    title: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: isMobile ? '14px' : '16px',
      color: '#6b7280',
      marginBottom: '32px',
      textAlign: 'center'
    },
    searchBox: {
      display: 'flex',
      gap: '12px',
      marginBottom: '32px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    input: {
      flex: 1,
      padding: '14px 18px',
      fontSize: '15px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      outline: 'none',
      transition: 'all 0.3s ease',
      backgroundColor: '#f9fafb',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontWeight: '600'
    },
    button: {
      padding: '14px 32px',
      fontSize: '16px',
      fontWeight: '600',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      minWidth: isMobile ? '100%' : '150px'
    },
    error: {
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      padding: '12px 16px',
      borderRadius: '10px',
      marginBottom: '24px',
      fontSize: '14px',
      fontWeight: '500',
      textAlign: 'center'
    },
    statusCard: {
      border: '2px solid #e5e7eb',
      borderRadius: '16px',
      padding: isMobile ? '24px' : '32px',
      marginTop: '24px'
    },
    statusHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 24px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    appNumber: {
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: '700',
      color: '#1f2937',
      letterSpacing: '2px'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '20px',
      marginBottom: '24px'
    },
    infoItem: {
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    infoLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '6px'
    },
    infoValue: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#1f2937'
    },
    remarksBox: {
      padding: '20px',
      backgroundColor: '#fef3c7',
      border: '2px solid #fbbf24',
      borderRadius: '12px',
      marginTop: '20px'
    },
    remarksTitle: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#92400e',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    remarksText: {
      fontSize: '15px',
      color: '#78350f',
      lineHeight: '1.6'
    },
    timeline: {
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '2px solid #e5e7eb'
    },
    timelineTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '16px'
    },
    timelineItem: {
      display: 'flex',
      gap: '12px',
      marginBottom: '12px'
    },
    timelineIcon: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
      flexShrink: 0
    },
    timelineContent: {
      flex: 1
    },
    timelineDate: {
      fontSize: '13px',
      color: '#6b7280',
      fontWeight: '600'
    },
    timelineText: {
      fontSize: '14px',
      color: '#374151',
      marginTop: '4px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button
          onClick={() => onNavigate('home')}
          style={styles.backButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#6366f1';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Home
        </button>
      </div>

      <div style={styles.mainCard}>
        <h1 style={styles.title}>Check Application Status</h1>
        <p style={styles.subtitle}>
          Enter your application number to track your registration status
        </p>

        <div style={styles.searchBox}>
          <input
            type="text"
            value={applicationNumber}
            onChange={(e) => {
              setApplicationNumber(e.target.value.toUpperCase());
              setError('');
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleCheckStatus()}
            style={styles.input}
            placeholder="APP2025000001"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
          <button
            onClick={handleCheckStatus}
            disabled={loading}
            style={{...styles.button, opacity: loading ? 0.7 : 1}}
            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
          >
            {loading ? 'Checking...' : 'Check Status'}
            {!loading && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            )}
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {application && (
          <div style={styles.statusCard}>
            <div style={styles.statusHeader}>
              <div style={styles.appNumber}>
                {application.applicationNumber}
              </div>
              <div
                style={{
                  ...styles.statusBadge,
                  backgroundColor: `${getStatusColor(application.status)}20`,
                  color: getStatusColor(application.status),
                  border: `2px solid ${getStatusColor(application.status)}`
                }}
              >
                <span>{getStatusIcon(application.status)}</span>
                {application.status.replace('-', ' ')}
              </div>
            </div>

            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Full Name</div>
                <div style={styles.infoValue}>{application.fullName}</div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Phone Number</div>
                <div style={styles.infoValue}>+91 {application.phoneNumber}</div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Email</div>
                <div style={styles.infoValue}>{application.email}</div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Course</div>
                <div style={styles.infoValue}>{application.course}</div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Department</div>
                <div style={styles.infoValue}>{application.department}</div>
              </div>


              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Room Type</div>
                <div style={styles.infoValue}>
                  {application.roomType 
                    ? application.roomType.charAt(0).toUpperCase() + application.roomType.slice(1) + ' Occupancy'
                    : 'Not Assigned'}
                </div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Applied Date</div>
                <div style={styles.infoValue}>{formatDate(application.appliedDate)}</div>
              </div>
            </div>

            {application.adminRemarks && (
              <div style={styles.remarksBox}>
                <div style={styles.remarksTitle}>
                  {application.status === 'approved' ? '✓ Admin Message' : '⚠️ Admin Remarks'}
                </div>
                <div style={styles.remarksText}>{application.adminRemarks}</div>
              </div>
            )}

            <div style={styles.timeline}>
              <div style={styles.timelineTitle}>Application Timeline</div>

              <div style={styles.timelineItem}>
                <div
                  style={{
                    ...styles.timelineIcon,
                    backgroundColor: '#10b981',
                    color: 'white'
                  }}
                >
                  ✓
                </div>
                <div style={styles.timelineContent}>
                  <div style={styles.timelineDate}>
                    {formatDate(application.appliedDate)}
                  </div>
                  <div style={styles.timelineText}>Application Submitted</div>
                </div>
              </div>

              {application.status === 'under-review' && (
                <div style={styles.timelineItem}>
                  <div
                    style={{
                      ...styles.timelineIcon,
                      backgroundColor: '#3b82f6',
                      color: 'white'
                    }}
                  >
                    🔍
                  </div>
                  <div style={styles.timelineContent}>
                    <div style={styles.timelineDate}>In Progress</div>
                    <div style={styles.timelineText}>Application Under Review</div>
                  </div>
                </div>
              )}

              {(application.status === 'approved' || application.status === 'rejected') && (
                <div style={styles.timelineItem}>
                  <div
                    style={{
                      ...styles.timelineIcon,
                      backgroundColor: application.status === 'approved' ? '#10b981' : '#ef4444',
                      color: 'white'
                    }}
                  >
                    {application.status === 'approved' ? '✓' : '✕'}
                  </div>
                  <div style={styles.timelineContent}>
                    <div style={styles.timelineDate}>
                      {formatDate(application.reviewedDate)}
                    </div>
                    <div style={styles.timelineText}>
                      Application {application.status === 'approved' ? 'Approved' : 'Rejected'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {application.status === 'approved' && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '20px',
                  backgroundColor: '#d1fae5',
                  border: '2px solid #6ee7b7',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>
                  🎉 Congratulations!
                </div>
                <div style={{ fontSize: '14px', color: '#047857', lineHeight: '1.6' }}>
                  Your application has been approved. You will receive your login credentials via email and SMS shortly.
                  Please check your inbox and spam folder.
                </div>
              </div>
            )}

            {application.status === 'pending' && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '20px',
                  backgroundColor: '#fef3c7',
                  border: '2px solid #fbbf24',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#92400e', lineHeight: '1.6' }}>
                  Your application is being reviewed. We will contact you within 24-48 hours.
                </div>
              </div>
            )}

            {application.status === 'rejected' && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '20px',
                  backgroundColor: '#fee2e2',
                  border: '2px solid #fecaca',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#991b1b', marginBottom: '12px' }}>
                  Unfortunately, your application was not approved at this time.
                </div>
                <button
                  onClick={() => onNavigate('student-register')}
                  style={{
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    border: 'none',
                    cursor: 'pointer',
                    background: '#dc2626',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                >
                  Apply Again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationStatusChecker;